import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

/**
 * OKS-50 — Responsive badge grid wrapper.
 *
 * Two ladders, documented as they ACTUALLY behave:
 *
 *   `preview`  — 2-col, 4-col from `sm`, back to 2-col from `lg`. Used by
 *                the /profile "My Badges" block, which is capped at 4
 *                badges (`Profile::ProfileProps`, `limit: 4`), so a 3-col
 *                mid-step would orphan a tile onto a second row while
 *                2-col always yields a balanced 2x2 (OKS-926, UX §2c) —
 *                which the `lg` step preserves rather than disturbs.
 *   default    — 2 / 3 (`sm`) / 4 (`md`) / 5 (`xl`). Used by the brand
 *                badge catalogue.
 *
 * OKS-972 — the preview branch's `lg` step DOWN to 2 exists because this
 * block renders inside the creator shell, which caps the app at a centred
 * 480px column from `lg` up (`CREATOR_APP_COLUMN`, OKS-739). From `lg` the
 * ladder therefore divides a FIXED 480px rather than the viewport, and
 * 4-up left a 103.0px tile / 69px name box — narrower than the same tile
 * at 320px. The default branch has no such cap (full-width backoffice) and
 * is deliberately untouched: its assertions staying green in
 * `__tests__/badge-grid.test.tsx` are what prove this edit stayed inside
 * the preview arm.
 *
 * NB the default ladder's breakpoints disagree with UX Spec §5 as
 * written (§5 documents 375/768/1440 against Tailwind's 640/768/1280).
 * Reconciling that is OKS-236 and is deliberately NOT touched here —
 * OKS-926 changed only the preview branch. This comment describes the
 * code, not the spec, precisely because the previous version described
 * the spec and was therefore wrong about both branches.
 *
 * Children are usually `<BadgeTile>` instances; the grid itself
 * doesn't gate which kind of tile renders (earned, locked, custom)
 * — the parent decides.
 */
interface BadgeGridProps {
  children: ReactNode
  className?: string
  /**
   * Selects the preview ladder (2-col, 4-col from `sm`).
   *
   * OKS-926 — was `fixedCols?: 4`, which stopped being true when the
   * preview gained a mobile step-down: a prop named "fixed" that yields
   * 2 columns at 320px misleads every future reader.
   */
  preview?: boolean
}

export function BadgeGrid({ children, className, preview }: BadgeGridProps) {
  return (
    <div
      data-testid="badge-grid"
      className={cn(
        "grid gap-3",
        preview
          ? "grid-cols-2 sm:grid-cols-4 lg:grid-cols-2"
          : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5",
        className,
      )}
    >
      {children}
    </div>
  )
}
